import { MembershipProfileTag } from '@models/membership/MembershipProfileTag'
import { membershipProfileTagSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createMembershipProfileTag as createMembershipProfileTagAPI,
  deleteMembershipProfileTag as deleteMembershipProfileTagAPI,
  fetchMembershipProfileTags,
  updateMembershipProfileTag as updateMembershipProfileTagAPI,
} from '@services/membership/MembershipProfileTagService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<MembershipProfileTag> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'membershipProfileTag',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getMembershipProfileTagsSuccess,
  setQuery: setMembershipProfileTagQuery,
  reset: resetMembershipProfileTagState,
  resetQueryAndIds: resetMembershipProfileTagQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: MembershipProfileTag }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectMembershipProfileTags = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedMembershipProfileTag = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectMembershipProfileTagById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectMembershipProfileTagsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedMembershipProfileTag =
  (MembershipProfileTag: MembershipProfileTag | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<MembershipProfileTag>(MembershipProfileTag)
      )
    )

export const getMembershipProfileTags = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchMembershipProfileTags(params),
    [membershipProfileTagSchema],
    slice.actions
  )

export const createMembershipProfileTag = (tag: MembershipProfileTag) =>
  SBAPI.CreateDispatched(
    createMembershipProfileTagAPI(tag),
    membershipProfileTagSchema,
    slice.actions
  )

export const updateMembershipProfileTag = (
  MembershipProfileTag: MembershipProfileTag
) =>
  SBAPI.UpdateDispatched<MembershipProfileTag>(
    updateMembershipProfileTagAPI(MembershipProfileTag),
    MembershipProfileTag,
    slice.actions
  )

export const deleteMembershipProfileTag = (
  MembershipProfileTag: MembershipProfileTag
) =>
  SBAPI.DestroyDispatched<MembershipProfileTag>(
    deleteMembershipProfileTagAPI(MembershipProfileTag),
    MembershipProfileTag,
    slice.actions
  )
